import React, { useCallback, useEffect, useReducer, useMemo, useContext } from 'react'
import moment from 'moment'
import qs from 'qs'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import { useDispatch, connect } from 'react-redux'
import type { AppDispatch } from 'src/client/store'
import { pushWithRegion } from 'actions/NavigationActions'
import { resetFlights } from 'actions/CartActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import flightSearchWidgetStateReducer, { initialState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetStateProvider from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateProvider'
import { FlightSearchWidgetState } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { validateOccupants } from 'lib/flights'
import { FlightsFareTypes } from 'constants/flight'
import { FlightsCreditDetails } from 'components/Flights/types'
import { isFlightsCredit } from 'selectors/flightsSelectors'
import { getPopularAirports } from 'actions/FlightActions'

import Breakpoint from 'components/Common/Breakpoint'
import FlightSearchWidgetDesktop from './FlightSearchWidgetDesktop'
import FlightSearchWidgetMobile from './FlightSearchWidgetMobile'
import { countOccupantsForFlights } from 'lib/occupancyUtils'
import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_FLIGHT } from 'constants/offer'

export enum StandaloneFlightMenu {
  departAirport = 'departAirport',
  arrivalAirport = 'arrivalAirport',
  date = 'date',
  occupants = 'occupants'
}

const Root = styled.form`
  color: ${props => props.theme.palette.neutral.one};
  display: flex;
  flex-direction: column;
  width: 100%;
`

interface Props {
  className?: string;
  searchData?: FlightSearchWidgetState;
  useOnlyMobile?: boolean
  creditDetails?: FlightsCreditDetails;
  isFlightsCredit: boolean;
  onSubmit?: () => void;
}

function FlightsSearchWidget(props: Props) {
  const dispatch = useDispatch<AppDispatch>()
  const { className, searchData, isFlightsCredit, creditDetails, useOnlyMobile, onSubmit } = props
  const { currentRegionCode } = useContext(GeoContext)

  const [state, stateDispatch] = useReducer(flightSearchWidgetStateReducer, {
    ...initialState,
    ...searchData,
  })

  useEffect(() => {
    if (searchData) {
      stateDispatch({
        type: FlightSearchWidgetActions.UPDATE_FLIGHT_SEARCH_WIDGET_DATA,
        payload: searchData,
      })
    }
  }, [searchData])

  const {
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    occupants,
    fareType,
    fareCabin,
    showDepartureError,
    showDesinationError,
    showTravelDatesError,
    showSameAirportError,
  } = state

  const invalidOccupantsSelection = useMemo(() => validateOccupants(occupants), [occupants])

  const invalidDateSelection = useMemo(() => {
    if (fareType.value === 'return') {
      return !checkinDate || !checkoutDate
    } else {
      return !checkinDate
    }
  }, [fareType, checkinDate, checkoutDate])

  const onSearchEvent = useContext(GlobalSearchTrackingContext)

  const onSearchAirport = useCallback(() => {
    if (!departureAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR, error: true })
    }
    if (!arrivalAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR, error: true })
    }
    if (invalidDateSelection) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR, error: true })
    }
    if (departureAirport?.airportCode === arrivalAirport?.airportCode) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR, error: true })
    }
    if (!departureAirport?.airportCode || !arrivalAirport?.airportCode ||
      invalidDateSelection || occupants.childrenAge?.some(age => age === -1) ||
      departureAirport?.airportCode === arrivalAirport?.airportCode || invalidOccupantsSelection) {
      return
    }
    onSubmit?.()

    const transformedOccupants = countOccupantsForFlights([occupants])
    const isReturnFare = fareType.value === FlightsFareTypes.RETURN

    const queryParams = {
      originAirportCode: departureAirport.airportCode,
      originAirportName: departureAirport.airportName,
      destinationAirportCode: arrivalAirport.airportCode,
      destinationAirportName: arrivalAirport.airportName,
      departDate: moment(checkinDate).format(ISO_DATE_FORMAT),
      returnDate: isReturnFare ? moment(checkoutDate).format(ISO_DATE_FORMAT) : null,
      adults: transformedOccupants.adults,
      children: transformedOccupants.children,
      infants: transformedOccupants.infants,
      childrenAge: transformedOccupants.childrenAge.join(','),
      fareClass: fareCabin.value,
      fareType: fareType.value,
      searchId: moment().unix(),
      carrier: isFlightsCredit ? creditDetails?.carrier : undefined,
      flightsCredit: isFlightsCredit || undefined,
    }

    if (onSearchEvent) {
      const params = {
        vertical: OFFER_TYPE_FLIGHT,
      }
      onSearchEvent('search-button', params)
    }

    dispatch(resetFlights())
    dispatch(pushWithRegion('/flights-search-results', qs.stringify(queryParams)))
  }, [
    onSubmit,
    departureAirport,
    arrivalAirport,
    invalidDateSelection,
    occupants,
    invalidOccupantsSelection,
    fareType.value,
    checkinDate,
    checkoutDate,
    fareCabin.value,
    isFlightsCredit,
    creditDetails?.carrier,
    dispatch,
    onSearchEvent,
  ])

  useEffect(() => {
    if (departureAirport?.airportCode && showDepartureError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_DEPARTURE_ERROR, error: false })
    }
    if (arrivalAirport?.airportCode && showDesinationError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_ARRIVAL_ERROR, error: false })
    }
    if (!invalidDateSelection && showTravelDatesError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_TRAVEL_DATES_ERROR, error: false })
    }
    if (departureAirport?.airportCode !== arrivalAirport?.airportCode && showSameAirportError) {
      stateDispatch({ type: FlightSearchWidgetActions.SHOW_SAME_AIRPORT_ERROR, error: false })
    }
  }, [
    departureAirport,
    arrivalAirport,
    checkinDate,
    checkoutDate,
    invalidDateSelection,
    showDepartureError,
    showDesinationError,
    showTravelDatesError,
    showSameAirportError,
  ])

  useEffect(() => {
    dispatch(getPopularAirports())
  }, [dispatch, currentRegionCode])

  return (
    <FlightSearchWidgetStateProvider state={state} dispatch={stateDispatch}>
      <Root className={className} onSubmit={(e) => e.preventDefault()}>
        {!useOnlyMobile && (
          <Breakpoint min="sm">
            <FlightSearchWidgetDesktop onSearchAirport={onSearchAirport} />
          </Breakpoint>
        )}

        <Breakpoint max={useOnlyMobile ? undefined : 'xs'}>
          <FlightSearchWidgetMobile
            useMobileForm={useOnlyMobile}
            onSearchAirport={onSearchAirport}
          />
        </Breakpoint>
      </Root>
    </FlightSearchWidgetStateProvider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    creditDetails: state.flights.creditDetails,
    isFlightsCredit: isFlightsCredit(state),
  }
}

export default connect(mapStateToProps)(FlightsSearchWidget)
